import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/pro-regular-svg-icons';
import {
    IanalyticsInspection,
    IanalyticsPMP,
    Ifacility,
    IinspectionDates
} from '../../models';
import { TFunction } from 'i18next';
import moment from 'moment';
import { runCodeDeficiencyReport } from '../../actions/manageReportActions';
import { setTableFilter } from '../../actions/manageInventoryActions';
import {
    measurementPointResultStatusTypesEnum,
    statusNameToEnumMap
} from '../../models-enums';
import PrintUpdatedListModal from './PrintUpdatedListModal';
import { useHistory } from 'react-router-dom';
import StatusCard from './StatusCard';
import { constants } from '../../constants/constants';
import { applyStatusLabelOverrides } from '../../helpers/statusLabelHelper';

interface StatusBarItem {
    label: string;
    value: number;
    color: string;
    className: string;
    statusEnum?: number;
}

interface OperationalStatusProps {
    inspection: IanalyticsInspection[];
    inspectionDates?: IinspectionDates | null;
    pmp: IanalyticsPMP;
    selectedFacility: Ifacility;
    selectedCategory: string;
    runCodeDeficiencyReport: typeof runCodeDeficiencyReport;
    setTableFilter: typeof setTableFilter;
    onStatusSegmentClick?: (statusEnum: number) => void;
    t: TFunction;
}

const OperationalStatus: React.FC<OperationalStatusProps> = ({
    inspection,
    inspectionDates,
    pmp,
    selectedFacility,
    selectedCategory,
    runCodeDeficiencyReport: runReport,
    setTableFilter: setFilter,
    onStatusSegmentClick,
    t
}) => {
    const history = useHistory();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [inspectionData, setInspectionData] = useState<StatusBarItem[]>([]);
    const [totalInspections, setTotalInspections] = useState(0);
    const [showPrintModal, setShowPrintModal] = useState(false);

    // Helper function to get quantity from status - memoized to prevent infinite loops
    const getQuantityFromStatus = useCallback(
        (status: string, data: any[], total: number): number => {
            const element = data.find(
                d => d.name?.toLowerCase() === status.toLowerCase()
            );
            if (element) {
                return Math.round((element.y / 100) * total);
            }
            return 0;
        },
        []
    );

    // Process inspection data - filter by selected category
    useEffect(() => {
        if (inspection && inspection.length > 0) {
            let selectedInspection;

            // If a specific category is selected, find that category's data
            if (selectedCategory !== 'All') {
                selectedInspection = inspection.find(
                    item => item.mainCategoryID === selectedCategory
                );
            } else {
                // If "All" is selected, use the "ALL" category to match legacy behavior
                selectedInspection = inspection.find(
                    item => item.mainCategoryID === constants.ALL_CATEGORY_ID
                );

                // If no "ALL" category exists, fall back to first category with data
                if (!selectedInspection || selectedInspection.total === 0) {
                    selectedInspection = inspection.find(
                        item => item.total > 0
                    );
                }
            }

            if (selectedInspection && selectedInspection.total > 0) {
                const { data, total } = selectedInspection;

                // Dynamically process data array from API
                const allStatusItems: StatusBarItem[] = data.map(
                    (item: any) => {
                        const statusNameLower = item.name?.toLowerCase() || '';
                        const quantity = getQuantityFromStatus(
                            statusNameLower,
                            data,
                            total
                        );

                        // API sends "Pass", "Fail", "NotTested", "CannotComplete", etc.
                        // Convert to camelCase for translation key (e.g., "NotTested" -> "notTested")
                        const translationKey = item.name
                            ? item.name.charAt(0).toLowerCase() +
                              item.name.slice(1)
                            : statusNameLower;

                        // Translate the status name via i18n (same as legacy component)
                        const translatedLabel = t(
                            `dashboardAnalytics:${translationKey}`
                        );

                        // Get color from constants lookup using translated label (same as legacy component)
                        const color =
                            constants.installBaseStatusColorLookup[
                                translatedLabel as keyof typeof constants.installBaseStatusColorLookup
                            ] || constants.colors.greyBlue;

                        // Apply display label overrides (e.g., "Fail" -> "Compliance Required")
                        const displayLabel = applyStatusLabelOverrides(
                            translatedLabel
                        );

                        return {
                            label: displayLabel,
                            value: quantity,
                            color,
                            className: statusNameLower.replace(/\s+/g, '-'),
                            statusEnum: statusNameToEnumMap[statusNameLower]
                        };
                    }
                );

                // Only include items with value > 0 for display
                const visibleItems = allStatusItems.filter(
                    item => item.value > 0
                );

                setInspectionData(visibleItems);
                setTotalInspections(total);
            } else {
                setInspectionData([]);
                setTotalInspections(0);
            }
        } else {
            setInspectionData([]);
            setTotalInspections(0);
        }
        // eslint-disable-next-line
    }, [inspection, selectedCategory, getQuantityFromStatus]);

    // Calculate maintenance data from PMP
    const getTotalFromData = (dataArray: any[]): number => {
        return dataArray.reduce((sum, item) => sum + (item.y || 0), 0);
    };

    const openWorkOrders = pmp?.data?.open
        ? getTotalFromData(pmp.data.open)
        : 0;
    const closedWorkOrders = pmp?.data?.closed
        ? getTotalFromData(pmp.data.closed)
        : 0;
    const totalPMP = openWorkOrders + closedWorkOrders;

    // For now, show work orders as OK/Due based on open status
    // TODO: This needs proper calculation based on due dates
    const maintenanceData: StatusBarItem[] =
        totalPMP > 0
            ? [
                  {
                      label: 'OK',
                      value: closedWorkOrders,
                      color: '#18AB2B',
                      className: 'ok'
                  },
                  {
                      label: 'Due Soon',
                      value: openWorkOrders,
                      color: '#FD9A24',
                      className: 'due-soon'
                  }
              ]
            : [];

    const totalActions = pmp?.totalWorkOrders || 0;
    const hasPMPData = pmp && pmp.totalWorkOrders > 0;

    // Check if we have any data to display
    const hasData = totalInspections > 0 || hasPMPData;

    const togglePrintModal = () => {
        setShowPrintModal(!showPrintModal);
    };

    const handlePrint = (date: moment.Moment) => {
        runReport(selectedFacility.id, date);
    };

    const navigateToAssetStatus = () => {
        history.push('/assetStatus');
    };

    const navigateToMaintenance = () => {
        history.push('/pmp');
    };

    const handleStatusClick = (statusEnum: number, statusLabel: string) => {
        // If onStatusSegmentClick callback is provided, use it to scroll to AssetDetailsView
        if (onStatusSegmentClick) {
            onStatusSegmentClick(statusEnum);
        } else {
            // Fallback to legacy behavior (navigate to inventory page)
            const translatedLabel = t(
                `manageInventory:${measurementPointResultStatusTypesEnum[statusEnum]}`
            );

            // Clear search and category filters, then set only the clicked status
            setFilter({
                search: '',
                mainCategory: undefined,
                status: {
                    value: `${statusEnum}`,
                    label: translatedLabel
                },
                hideVirtualToggle: true,
                page: 0
            });

            history.push('/inventory');
        }
    };

    if (!hasData) {
        return null;
    }

    return (
        <div className="operational-status-section">
            <div className="operational-status-header">
                <h2>Operational Status</h2>
                <Button
                    bsStyle="link"
                    className="collapse-button"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                >
                    {isCollapsed ? 'Expand' : 'Collapse'}
                    <FontAwesomeIcon
                        icon={
                            isCollapsed
                                ? ['fas', 'chevron-down']
                                : ['fas', 'chevron-up']
                        }
                        className="collapse-icon"
                    />
                </Button>
            </div>

            {!isCollapsed && (
                <div className="operational-status-content">
                    {/* Inspection Status Card */}
                    {totalInspections > 0 && (
                        <StatusCard
                            title="Inspection Status"
                            subtitle={`${inspectionData.reduce(
                                (sum, i) => sum + i.value,
                                0
                            )} Assets`}
                            statusData={inspectionData}
                            onStatusClick={handleStatusClick}
                            onInfoClick={navigateToAssetStatus}
                            infoIconTitle="View Asset Status"
                            showInfoIcon={true}
                            showLegendInFooter={true}
                            dateItems={
                                inspectionDates
                                    ? [
                                          {
                                              label: t(
                                                  'dashboardAnalytics:lastInspection'
                                              ),
                                              value:
                                                  inspectionDates.lastInspectionDate,
                                              isTBD:
                                                  inspectionDates.lastInspectionDate ===
                                                  'TBD'
                                          },
                                          {
                                              label: t(
                                                  'dashboardAnalytics:nextInspection'
                                              ),
                                              value:
                                                  inspectionDates.nextInspectionDate,
                                              isTBD:
                                                  inspectionDates.nextInspectionDate ===
                                                  'TBD'
                                          }
                                      ]
                                    : undefined
                            }
                            actionButtons={
                                <Button
                                    className="print-button"
                                    onClick={togglePrintModal}
                                >
                                    <FontAwesomeIcon icon={faPrint} />
                                    <span>
                                        {t(
                                            'dashboardAnalytics:printUpdatedList'
                                        )}
                                    </span>
                                </Button>
                            }
                        />
                    )}

                    {/* Maintenance Actions Card */}
                    {hasPMPData && (
                        <StatusCard
                            title="Maintenance Actions"
                            subtitle={`${totalActions} Actions Required`}
                            statusData={maintenanceData}
                            onInfoClick={navigateToMaintenance}
                            infoIconTitle="View Maintenance Page"
                            showInfoIcon={true}
                            showLegendInFooter={false}
                        />
                    )}
                </div>
            )}
            <PrintUpdatedListModal
                show={showPrintModal}
                onHide={togglePrintModal}
                onPrint={handlePrint}
                title={t('dashboardAnalytics:printListModalTitle')}
                className="print-updated-list-modal"
            />
        </div>
    );
};

export default OperationalStatus;
